import { useEffect } from 'react';
import { toast } from 'react-toastify';

// Replaces the browser's native "Please fill out this field" bubble. Native
// constraint validation still runs and still blocks the submit — we only
// swap the presentation: focus the offending field, give it the red
// aria-invalid ring, and show the message as a themed toast.
//
// Hidden proxy inputs (Radix Select's bubble <select>, DatePicker's required
// proxy) point at their visible trigger via data-fd-for="<name>".
const labelFor = (field) => {
    if (field.labels && field.labels[0]) return field.labels[0].textContent;
    const aria = field.getAttribute('aria-label');
    if (aria) return aria;
    // Common layout here: <div><label>Name</label><input/></div>
    let node = field.parentElement;
    for (let i = 0; node && i < 3; i++, node = node.parentElement) {
        const label = node.querySelector(':scope > label, :scope > .form-label, :scope > .fd-field-label');
        if (label && label.textContent.trim()) return label.textContent;
    }
    return field.getAttribute('placeholder') || '';
};

export default function ValidationHost() {
    useEffect(() => {
        let last = 0;
        const onInvalid = (e) => {
            const el = e.target;
            if (!(el instanceof HTMLElement)) return;
            e.preventDefault();
            // Only the first invalid field of a submit attempt gets attention.
            const now = Date.now();
            if (now - last < 400) return;
            last = now;

            const form = el.form || el.closest('form') || document;
            const proxyTarget = el.name
                ? form.querySelector(`[data-fd-for="${window.CSS && CSS.escape ? CSS.escape(el.name) : el.name}"]`)
                : null;
            const target = proxyTarget || el;

            target.setAttribute('aria-invalid', 'true');
            const clear = () => {
                target.removeAttribute('aria-invalid');
                ['input', 'change', 'click', 'keydown'].forEach(t => target.removeEventListener(t, clear));
            };
            ['input', 'change', 'click', 'keydown'].forEach(t => target.addEventListener(t, clear));
            if (typeof target.focus === 'function') target.focus({ preventScroll: false });
            target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });

            const label = labelFor(target === el ? el : target).replace(/[*:]/g, '').replace(/\s+/g, ' ').trim();
            const message = el.validity && el.validity.valueMissing
                ? `${label || 'This field'} is required.`
                : `${label ? `${label}: ` : ''}${el.validationMessage}`;
            toast.error(message, { toastId: 'fd-form-invalid', autoClose: 3500 });
        };
        document.addEventListener('invalid', onInvalid, true);
        return () => document.removeEventListener('invalid', onInvalid, true);
    }, []);

    return null;
}
